import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { uploadLogo, getLogoUrl } from "@/lib/upload-logo";
import { useMutation } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { Camera, Loader2, X } from "lucide-react";

interface LogoUploadProps {
  empresaId: string;
  currentLogoUrl?: string | null;
  session: any;
  size?: "sm" | "md" | "lg";
  onUploaded?: (url: string) => void;
}

export function LogoUpload({ empresaId, currentLogoUrl, session, size = "md", onUploaded }: LogoUploadProps) {
  const { toast } = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [imgError, setImgError] = useState(false);

  const sizeClasses = {
    sm: "w-12 h-12",
    md: "w-20 h-20",
    lg: "w-28 h-28",
  };

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const url = await uploadLogo(empresaId, file);
      const res = await fetch(`/api/empresas/${empresaId}/logo`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ logoUrl: url }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Erro ao salvar logo");
      }
      return url;
    },
    onSuccess: (url) => {
      queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      queryClient.invalidateQueries({ queryKey: ["/api/empresas"] });
      toast({ title: "Logo atualizada", description: "A logo da empresa foi salva com sucesso." });
      setImgError(false);
      onUploaded?.(url);
    },
    onError: (err: Error) => {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
      setPreview(null);
    },
  });

  const handleFile = (file: File) => {
    if (!file.type.startsWith("image/")) {
      toast({ title: "Formato inválido", description: "Selecione uma imagem (PNG, JPG, etc.)", variant: "destructive" });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: "Arquivo grande", description: "O tamanho máximo é 5MB.", variant: "destructive" });
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target?.result as string);
    reader.readAsDataURL(file);
    uploadMutation.mutate(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  const displayUrl = preview || (currentLogoUrl && !imgError ? currentLogoUrl : null);

  return (
    <div className="flex items-center gap-4">
      <div
        className={`${sizeClasses[size]} rounded-xl border-2 border-dashed border-slate-200 flex items-center justify-center overflow-hidden cursor-pointer hover:border-primary/50 transition-colors relative group bg-slate-50`}
        onClick={() => fileRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        data-testid="logo-upload-area"
      >
        {uploadMutation.isPending ? (
          <Loader2 className="w-6 h-6 text-muted-foreground animate-spin" />
        ) : displayUrl ? (
          <>
            <img
              src={displayUrl}
              alt="Logo"
              className="w-full h-full object-contain"
              onError={() => setImgError(true)}
              data-testid="img-logo-preview"
            />
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <Camera className="w-5 h-5 text-white" />
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center gap-1 text-muted-foreground">
            <Camera className="w-5 h-5" />
            <span className="text-[10px]">Logo</span>
          </div>
        )}
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleChange}
        data-testid="input-logo-file"
      />
      <div className="text-xs text-muted-foreground">
        <p>Clique ou arraste para enviar</p>
        <p>PNG, JPG - máx 5MB</p>
      </div>
    </div>
  );
}

export function LogoAvatar({ empresaId, logoUrl, fallback, size = "sm" }: {
  empresaId: string;
  logoUrl?: string | null;
  fallback: string;
  size?: "xs" | "sm" | "md";
}) {
  const [imgError, setImgError] = useState(false);
  const sizeClasses = {
    xs: "w-8 h-8",
    sm: "w-10 h-10",
    md: "w-12 h-12",
  };
  const textSizes = {
    xs: "text-xs",
    sm: "text-sm",
    md: "text-base",
  };

  const url = logoUrl || getLogoUrl(empresaId);

  if (imgError) {
    return (
      <div className={`${sizeClasses[size]} rounded-lg bg-slate-100 flex items-center justify-center`}>
        <span className={`${textSizes[size]} font-bold text-slate-400`}>{fallback}</span>
      </div>
    );
  }

  return (
    <div className={`${sizeClasses[size]} rounded-lg overflow-hidden bg-slate-100 flex items-center justify-center`}>
      <img
        src={url}
        alt="Logo"
        className="w-full h-full object-contain"
        onError={() => setImgError(true)}
        data-testid={`img-logo-${empresaId}`}
      />
    </div>
  );
}
